/**
 * 어시스트 하단 드로어(D4) — **참가자 화면 전용**.
 *
 * `CoachDock` 을 쓰지 않는 이유(D4 근거):
 *  · `viewer.tsx` 는 `AppShell` 을 쓰지 않는다 — 도크는 `rightPanel` 슬롯
 *    전제로 짜여 있어 이식하면 폭 계산이 근거를 잃는다.
 *  · 도크는 `lg:flex`·`sm:flex` 로 **휴대폰에서 통째로 사라진다**. 참가자
 *    다수는 휴대폰이다 — "참가자도 보이게" 라는 요구 자체가 무효가 된다.
 *  · 자막 폭을 뺏지 않는다. 뷰어 화면은 자막이 본론이다.
 *
 * 그래서 `ChatPanel` 과 **같은 부류의 하단 고정 드로어**로 만들고, 본문은
 * `AssistPanel` 을 props 그대로 재사용한다(호스트와 같은 화면 규율).
 */

import { MessageCircleQuestion, X } from 'lucide-react'
import { AssistPanel } from './assist-panel'
import { AssistResizeHandle } from './assist-resize-handle'
import { useAssistCollapse } from '../lib/use-assist-collapse'
import { useAssistSize } from '../lib/use-assist-size'
import type { AssistItem } from '../lib/reducer'

export function AssistDrawer({
  items,
  onAsk,
  pending,
  hidden,
}: {
  items: AssistItem[]
  /** 질의 전송 — 거절 사유 문장을 던지면 `AssistPanel` 이 화면에 싣는다. */
  onAsk: (q: string) => Promise<void>
  /** 전송 중 — 중복 발사를 막는다 */
  pending: boolean
  /**
   * 발표 모드(D4-3) — 손잡이째 그리지 않는다. **언마운트가 아니라 숨김**
   * 이므로 접기 상태와 입력은 해제하면 그대로 돌아온다.
   */
  hidden: boolean
}): React.ReactElement {
  const { collapsed, setCollapsed } = useAssistCollapse()
  const { size, narrow, bounds, preview, commit } = useAssistSize()
  const open = !collapsed

  return (
    <div hidden={hidden}>
      {/* 손잡이 — 평소에는 이것만 보인다 */}
      <button
        type="button"
        onClick={() => setCollapsed(open)}
        aria-label={open ? '질문 닫기' : '질문 열기'}
        aria-expanded={open}
        data-testid="assist-toggle"
        className="fixed bottom-5 right-20 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-secondary)] text-[var(--color-secondary-foreground)] shadow-lg transition-transform hover:scale-105"
      >
        {open ? (
          <X className="h-5 w-5" />
        ) : (
          <MessageCircleQuestion className="h-5 w-5" />
        )}
      </button>

      {open && (
        <section
          data-testid="assist-drawer"
          aria-label="회의 질문"
          style={{ width: size.w, height: size.h }}
          className="fixed bottom-20 right-5 z-40 flex flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] shadow-xl"
        >
          <AssistResizeHandle
            narrow={narrow}
            bounds={bounds}
            size={size}
            onPreview={preview}
            onCommit={commit}
          />
          {/*
            O-2 안내 — 익명 주체의 지문은 재접속으로 바뀔 수 있다. 조용한
            유실이 아니라 "표시 안 됨" 이므로 화면이 먼저 말한다.
          */}
          <p
            data-testid="assist-reconnect-note"
            className="border-b border-[var(--color-border)] px-3 py-2 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
          >
            새로고침하면 이전 질문은 보이지 않을 수 있습니다.
          </p>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <AssistPanel items={items} onAsk={onAsk} pending={pending} />
          </div>
        </section>
      )}
    </div>
  )
}
